import { memo, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Task } from '../lib/types'
import { PRIORITY_LABELS, formatDue, isOverdue, priorityMark } from '../lib/format'
import { isPending } from '../lib/optimistic'
import { TaskKey } from './TaskKey'
import { Avatar, TagChip, cx } from './ui'

/**
 * How long a press has to be held before it starts selecting. Long enough not
 * to fire on a click, short enough not to feel broken. Any movement cancels it,
 * so a drag is never mistaken for a selection.
 */
const LONG_PRESS_MS = 450
const LONG_PRESS_SLOP = 6

/**
 * A board card.
 *
 * The key sits above the title rather than beside it, so a column of cards
 * lines its keys up into a monospace rail you can scan the way you scan a log.
 * Priority is a mono marker (`!!` / `!`) instead of a colour, which keeps the
 * card's colour budget for the things that genuinely signal: tags, and a red
 * edge when something is urgent or late.
 */
export const TaskCard = memo(function TaskCard({
  task, onOpen, pulsing, dragging, showProject,
  selected, selectionActive, onToggleSelect,
}: {
  task: Task
  onOpen: (task: Task) => void
  pulsing?: boolean
  dragging?: boolean
  /** On a board merging sub-projects, say which one a card came from. */
  showProject?: boolean
  selected?: boolean
  /** Something is selected, so cards show checkboxes and click means select. */
  selectionActive?: boolean
  onToggleSelect?: () => void
}) {
  const sortable = useSortable({ id: task.id, data: { type: 'task', task } })
  const overdue = isOverdue(task)
  const draft = isPending(task.id)
  const mark = priorityMark(task.priority)

  // Set by a completed long press so the click it produces does not also open
  // the task.
  const longPressed = useRef(false)

  function startLongPress(e: React.PointerEvent) {
    if (!onToggleSelect) return
    const origin = { x: e.clientX, y: e.clientY }
    longPressed.current = false

    const stop = () => {
      window.clearTimeout(timer)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
    }
    // Movement means a drag, not a selection.
    const onMove = (ev: PointerEvent) => {
      if (Math.hypot(ev.clientX - origin.x, ev.clientY - origin.y) > LONG_PRESS_SLOP) stop()
    }
    const timer = window.setTimeout(() => {
      stop()
      longPressed.current = true
      navigator.vibrate?.(15)
      onToggleSelect?.()
    }, LONG_PRESS_MS)

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
  }

  const listeners = sortable.listeners

  return (
    <article
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
      }}
      {...sortable.attributes}
      {...listeners}
      onPointerDown={(e) => {
        listeners?.onPointerDown?.(e)
        startLongPress(e)
      }}
      // Keyboard users reach cards by tab and open them with Enter; dnd-kit's
      // keyboard sensor handles moving them with space and the arrow keys.
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onOpen(task)
        }
      }}
      onClick={(e) => {
        if (longPressed.current) {
          // The click that ends a long press must not also open the task.
          longPressed.current = false
          return
        }
        // Once anything is selected, a click adds to or removes from the
        // selection rather than opening — the same way file managers behave.
        // Cmd/Ctrl-click starts a selection without waiting for a hold.
        if ((selectionActive || e.metaKey || e.ctrlKey) && onToggleSelect) {
          e.preventDefault()
          onToggleSelect()
          return
        }
        // A card that the server has not acknowledged has no id to open.
        if (draft) return
        onOpen(task)
      }}
      aria-label={`${task.key}: ${task.title}`}
      aria-selected={selectionActive ? Boolean(selected) : undefined}
      className={cx(
        'group relative cursor-pointer touch-manipulation select-none rounded-[7px] border bg-[var(--surface)]',
        'px-3 py-2.5 transition-[border-color,box-shadow,opacity] duration-[140ms]',
        'hover:border-[var(--line-strong)]',
        sortable.isDragging ? 'opacity-35' : 'opacity-100',
        dragging && 'rotate-[1.5deg] border-[var(--accent)] shadow-lg shadow-black/15',
        overdue || task.priority === 'urgent'
          ? 'border-[var(--line)] border-l-[2.5px] border-l-[var(--danger)]'
          : 'border-[var(--line)]',
        selected && 'border-[var(--accent)] ring-2 ring-[var(--accent)] ring-offset-1 ring-offset-[var(--sunken)]',
        pulsing && 'live-pulse',
        task.archivedAt && 'opacity-60',
        // Still in flight: dimmed enough to notice, not enough to distract.
        // It resolves in well under a second or it turns back into nothing.
        draft && 'opacity-55',
      )}
    >
      <header className="flex items-center gap-1.5">
        {selectionActive && (
          <input
            type="checkbox"
            checked={Boolean(selected)}
            readOnly
            tabIndex={-1}
            aria-hidden
            className="pointer-events-none h-4 w-4 shrink-0 accent-[var(--accent)]"
          />
        )}
        {draft ? (
          // The key is the server's to assign. Until it answers, the slot
          // holds its place rather than letting the title jump up a line.
          <span className="task-key text-[12px] text-[var(--ink-3)]" title="Saving…">
            ·&#8202;·&#8202;·
          </span>
        ) : (
          <TaskKey taskKey={task.key} className="text-[var(--ink-2)]" />
        )}
        {mark && (
          <span
            title={PRIORITY_LABELS[task.priority]}
            className="task-key text-[12px] font-bold leading-none text-[var(--danger)]"
          >
            {mark}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          {task.dueDate && (
            <span
              className={cx(
                'task-key text-[11.5px] leading-none',
                overdue ? 'font-semibold text-[var(--danger)]' : 'text-[var(--ink-3)]',
              )}
            >
              {formatDue(task.dueDate)}
            </span>
          )}
          <Avatar user={task.assignee} size={20} />
        </span>
      </header>

      <p className="mt-1 text-[14.5px] leading-snug text-[var(--ink)]">{task.title}</p>

      {showProject && (
        <p className="mt-1 truncate text-[12px] text-[var(--ink-3)]">{task.projectName}</p>
      )}

      {task.tags.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {task.tags.slice(0, 4).map((tag) => (
            <TagChip key={tag.id} tag={tag} />
          ))}
          {task.tags.length > 4 && (
            <span className="task-key self-center text-[11px] text-[var(--ink-3)]">
              +{task.tags.length - 4}
            </span>
          )}
        </div>
      )}

      {(task.commentCount > 0 || task.attachmentCount > 0 || task.subtaskCount > 0) && (
        <footer className="mt-1.5 flex items-center gap-2.5 text-[var(--ink-3)]">
          {task.subtaskCount > 0 && (
            <span
              className={cx(
                'task-key text-[11.5px]',
                // A finished checklist is worth noticing from the board.
                task.subtaskDone === task.subtaskCount && 'text-[var(--good)]',
              )}
              title={`${task.subtaskDone} of ${task.subtaskCount} subtasks done`}
            >
              ☑ {task.subtaskDone}/{task.subtaskCount}
            </span>
          )}
          {task.commentCount > 0 && (
            <span className="flex items-center gap-1" title={`${task.commentCount} comments`}>
              <CommentGlyph />
              <span className="task-key text-[11.5px]">{task.commentCount}</span>
            </span>
          )}
          {task.attachmentCount > 0 && (
            <span className="flex items-center gap-1" title={`${task.attachmentCount} attachments`}>
              <ClipGlyph />
              <span className="task-key text-[11.5px]">{task.attachmentCount}</span>
            </span>
          )}
        </footer>
      )}
    </article>
  )
})

const glyph = {
  width: 12, height: 12, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
}

function CommentGlyph() {
  return (
    <svg {...glyph} aria-hidden>
      <path d="M14 9.5a2 2 0 0 1-2 2H6l-3.5 2.5V4a2 2 0 0 1 2-2h7.5a2 2 0 0 1 2 2Z" />
    </svg>
  )
}

function ClipGlyph() {
  return (
    <svg {...glyph} aria-hidden>
      <path d="M11.5 6.5 6.9 11.1a2.05 2.05 0 0 1-2.9-2.9l5-5a3 3 0 0 1 4.25 4.25l-5 5a4 4 0 0 1-5.65-5.65l4.6-4.6" />
    </svg>
  )
}
